import React, { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";
import {
  isPatchColumn,
  isPatchMonitoringRoute,
} from "pages/patchMonitoring/patchMonitoring.config";

import useTransmissionTableColumn from "components/pages/transmissions/table/hooks/useTransmissionTableColumn";
import {
  changeCount,
  setReportList,
  setReportsMeta,
} from "app/store/transmission/slice/transmissionSlice";
import { DEFAULT_LARGE_PAGE_SIZE_OPTIONS } from "static/pagination";

import CustomTable from "components/common/table/CustomTable";
import TransmissionToolbar from "./components/TransmissionToolbar";
import { useGetReportsQuery } from "app/store/transmission/api/transmissionTableApi";
import { rowStyle } from "components/pages/transmissions/table/transmissionTableRowStyle";

import useTransmissionTable from "./hooks/useTransmissionTable";
import { TRANSMISSION_TAB_COUNT_ACCESS } from "static/constants";
import ReportIntervalFilter from "./components/ReportIntervalFilter";

const TransmissionTable = ({
  role,
  view,
  theme,
  rowSelection,
  setRowSelection,
  // Set only by the patch worklist.
  patch,
}) => {
  const { pathname } = useLocation();
  const {
    url,
    isReady,
    dispatch,
    isShowFilter,
    columnFilters,
    sorting,
    pagination,
    search,
    reportInterval,
    useReportIntervalFilter,

    onSortingChange,
    setColumnFilters,

    onSearchChange,
    setPagination,
    onRowClick,
    handleSelection,
    columnVisibility,
    setColumnVisibility,
  } = useTransmissionTable(role, view, setRowSelection, { patch });

  const { columns: allColumns } = useTransmissionTableColumn(view);

  // The patch worklist shows a subset of these columns. Filtering the shared set keeps
  // cell renderers, sorting and filters identical on both pages.
  const columns = useMemo(() => {
    if (!isPatchMonitoringRoute(pathname)) {
      return allColumns;
    }

    return (allColumns || []).filter((column) =>
      isPatchColumn(column?.accessorKey || column?.id)
    );
  }, [allColumns, pathname]);

  const { data, currentData, isLoading, isFetching } = useGetReportsQuery(
    { view, url },
    { skip: !isReady, refetchOnMountOrArgChange: true }
  );

  const reportList = data?.data;

  useEffect(() => {
    if (data) {
      dispatch(setReportList(reportList || []));
    }
  }, [data?.data]);

  useEffect(() => {
    dispatch(setReportsMeta(data?.meta || {}));
  }, [data?.meta]);

  useEffect(() => {
    if (
      currentData &&
      !isFetching &&
      TRANSMISSION_TAB_COUNT_ACCESS.includes(view)
    ) {
      dispatch(changeCount({ view, count: currentData?.meta?.total ?? 0 }));
    }
  }, [currentData, isFetching, dispatch, view]);

  const rowVirtualizerInstanceRef = useRef(null);

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex(0);
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    } catch (e) {
      console.error(e);
    }
  }, [pagination, reportInterval]);

  return (
    <CustomTable
      columns={columns}
      data={reportList || []}
      localization={{
        toolbar: { searchPlaceholder: "Search Report" },
      }}
      style={{
        border: "1px solid #e0e0e0",
        boxShadow: "none",
      }}
      options={{
        enableTopToolbar: true,
        enableSorting: true,
        enableRowSelection: true,
        enableBottomToolbar: true,
        enableRowVirtualization: true,
        rowVirtualizerInstanceRef,
        rowCount: data?.meta?.total || 0,
        isMultiSortEvent: () => true,
        state: {
          isLoading: isLoading || isFetching || !isReady,
          showGlobalFilter: true,
          showColumnFilters: isShowFilter,
          globalFilter: search,
          pagination,
          columnFilters,
          sorting,
          columnVisibility,
          rowSelection,
        },
        defaultColumn: {
          minSize: 20, //allow columns to get smaller than default
          maxSize: 9001, //allow columns to get larger than default
          size: 140, //make columns wider by default
        },
        displayColumnDefOptions: {
          "mrt-row-select": {
            size: 40,
          },
        },
        onColumnVisibilityChange: setColumnVisibility,
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,
        onGlobalFilterChange: onSearchChange,
        onPaginationChange: setPagination,
        onSortingChange: onSortingChange,
        onColumnFiltersChange: setColumnFilters,
        rowsPerPageOptions: DEFAULT_LARGE_PAGE_SIZE_OPTIONS,
        onRowSelectionChange: setRowSelection,
        renderEmptyRowsFallback: () => undefined,
        renderTopToolbar: ({ table }) => <TransmissionToolbar table={table} />,
        ...(view === "processed" && useReportIntervalFilter
          ? {
              renderBottomToolbarCustomActions: ({ table }) => (
                <div style={{ flexGrow: 1 }}>
                  <ReportIntervalFilter />
                </div>
              ),
            }
          : {}),
        onSelectionChange: (e) => handleSelection(e),
        muiSearchTextFieldProps: {
          onPointerCancel: () => {
            onSearchChange("");
          },
        },
        getRowId: (row) => row?._id,
        muiTableBodyRowProps: ({ row }) => {
          const rowData = row?.original;

          return {
            onClick: () => onRowClick(rowData),
            sx: {
              cursor: "pointer",
              "&.MuiTableRow-root": rowStyle(rowData, role, view, theme),
              "&.MuiTableRow-root.Mui-selected td:after": {
                background: "none !important",
              },
              "td:nth-of-type(3)": {
                paddingLeft: "16px",
                paddingRight: "16px",
              },
              "td:nth-of-type(3):hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            },
          };
        },
      }}
    />
  );
};

export default TransmissionTable;
